"use client";
import React, { useState } from "react";
import {
  Box,
  Container,
  Typography,
  Button,
  Chip,
} from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import Image from "next/image";
import "../app/globals.css";
import { useRouter } from "next/navigation";
import { useTranslation } from "react-i18next";
import { IMAGES } from "../constants/images";
import NextLink from "next/link";
const AIToolkit = () => {
  const router = useRouter();
  const { t, i18n } = useTranslation();
  const isArabic = i18n.language === "ar";
  const [activeTab, setActiveTab] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const tabs = [
    {
      id: 0,
      iconLink: IMAGES.TOOLKIT_ICON_1,
      tabKey: "tab0",
      image: IMAGES.TOOLKIT_IMG_1,
      link: "/features/candidate-sourcing-scouting",
      imageAlt:
        "Smart job posting automation with best AI recruiting tool and recruitment automation software",
    },
    {
      id: 1,
      iconLink: IMAGES.TOOLKIT_ICON_2,
      tabKey: "tab1",
      image: IMAGES.TOOLKIT_IMG_2,
      link: "/features/screening-matching",
      imageAlt:
        "AI-powered resume screening using top recruiting software and automated recruiting technology",
    },
    {
      id: 2,
      iconLink: IMAGES.TOOLKIT_ICON_3,
      tabKey: "tab2",
      image: IMAGES.TOOLKIT_IMG_3,
      link: "/features/applicant-tracking-system",
      imageAlt:
        "Zayn AI recruiter conducting automated video interviews in 50+ languages with best recruiting automation software",
    },
    {
      id: 3,
      iconLink: IMAGES.TOOLKIT_ICON_4,
      tabKey: "tab3",
      image: IMAGES.TOOLKIT_IMG_4,
      link: "/features/ai-recruiter-video-interviews",
      imageAlt:
        "Advanced anti-cheating technology in AI recruiting tool for authentic candidate assessment",
    },
    {
      id: 4,
      iconLink: IMAGES.TOOLKIT_ICON_5,
      tabKey: "tab4",
      image: IMAGES.TOOLKIT_IMG_5,
      link: "/features/team-collaboration-communication",
      imageAlt:
        "Complete candidate management system superior to traditional ATS using recruitment automation software",
    },
    {
      id: 5,
      iconLink: IMAGES.TOOLKIT_ICON_6,
      tabKey: "tab5",
      image: IMAGES.TOOLKIT_IMG_6,
      link: "/features/analytics-reporting",
      imageAlt:
        "Real-time recruiting analytics dashboard with best AI recruiting tool and top recruiting software",
    },
  ];
  const handleTabChange = (newTabId) => {
    if (newTabId === activeTab) return;
    setIsTransitioning(true);
    setActiveTab(newTabId);
    setTimeout(() => setIsTransitioning(false), 350);
  };

  // Auto-advance removed — tabs now change only via manual click / arrows.

  // Optional per-tab bullet list of features. Only tabs that have a
  // "features" array in the translation file will render this list.
  const activeFeatures = t(
    `aiToolkit.tabs.${tabs[activeTab].tabKey}.features`,
    { returnObjects: true },
  );
  const featuresList = Array.isArray(activeFeatures) ? activeFeatures : null;

  return (
    <Box
      sx={{
        py: { xs: 3, sm: 4, md: 5, lg: 3 },
        direction: isArabic ? "rtl" : "ltr",
      }}
    >
      <Container maxWidth="xl">
        {/* Header */}
        <Box
          sx={{
            textAlign: isArabic ? "center" : { xs: "left", lg: "center" },
            mb: { xs: 3, sm: 4, md: 5, lg: 8 },
            px: { xs: 2, sm: 3, md: 0 },
          }}
        >
          <Chip
            icon={
              <Box sx={{ width: 20, height: 20, position: "relative" }}>
                <Image
                  src={IMAGES.TOOLKIT_ICON}
                  alt="Toolkit"
                  width={20}
                  height={20}
                  style={{ objectFit: "contain" }}
                />
              </Box>
            }
            label={t("aiToolkit.chip")}
            sx={{
              bgcolor: "#f7f2ff",
              color: "#9048FB",
              fontWeight: 500,
              fontSize: { xs: "11px", sm: "12px", md: "13px", lg: "15px" },
              mb: { xs: 1.5, sm: 2, md: 2.3, lg: 3 },
              px: { xs: 1, sm: 1.2, md: 1.4, lg: 1.7 },
              py: { xs: 1.5, sm: 1.8, md: 2, lg: 2.5 },
              fontFamily: "var(--font-outfit)",
              borderRadius: "30px",
            }}
          />

          <Typography
            variant="h3"
            sx={{
              fontSize: {
                xs: "20px",
                sm: "24px",
                md: "30px",
                lg: "35px",
                xl: "40px",
              },
              fontWeight: 700,
              lineHeight: 1.2,
              color: "#020617",
              mb: { xs: 1.5, sm: 2 },
              fontFamily: "var(--font-plusjakarta)",
              display: "flex",
              alignItems: "center",
              justifyContent: isArabic
                ? "center"
                : { xs: "flex-start", lg: "center" },
              flexWrap: "wrap",
            }}
          >
            {!isArabic && (
              <Box
                component="span"
                sx={{
                  display: { xs: "none", lg: "inline-flex" },
                  alignItems: "center",
                  mr: { xs: 1, sm: 1.5, md: 2 },
                  height: "1em",
                  position: "relative",
                  pt: ".4%",
                }}
              >
                <Box
                  sx={{
                    width: { xs: 12, sm: 13, md: 14, lg: 15 },
                    height: { xs: 12, sm: 13, md: 14, lg: 15 },
                    borderRadius: "50%",
                    background:
                      "linear-gradient(135deg, #FF6B6B 0%, #FF0000 100%)",
                    position: "relative",
                    animation: "pulse 2s ease-in-out infinite",
                    "@keyframes pulse": {
                      "0%, 100%": {
                        transform: "scale(1)",
                        boxShadow: "0 0 0 0 rgba(255, 107, 107, 0.7)",
                      },
                      "50%": {
                        transform: "scale(1.1)",
                        boxShadow: "0 0 20px 10px rgba(255, 107, 107, 0)",
                      },
                    },
                  }}
                />
              </Box>
            )}
            {t("aiToolkit.title1")}
            <span
              style={{
                color: "#775CE5",
                marginLeft: ".5%",
                marginRight: ".5%",
              }}
            >
              {t("aiToolkit.titleHighlight")}
            </span>
            {t("aiToolkit.title2")}
          </Typography>

          <Typography
            sx={{
              color: "#02061799",
              mb: 0.7,
              fontWeight: "400",
              fontSize: {
                xs: "14px",
                sm: "15px",
                md: "17px",
                lg: "19px",
                xl: "22px",
              },
              fontFamily: "var(--font-outfit)",
              mx: isArabic ? "auto" : { xs: 0, lg: "auto" },
              textAlign: isArabic ? "center" : { xs: "left", lg: "center" },
              maxWidth: { xs: "100%", lg: 1000 },
              mt: { xs: 1.5, sm: 2, md: 2.5 },
              lineHeight: 1.5,
            }}
          >
            {t("aiToolkit.subtitle")}
          </Typography>

          <Typography
            sx={{
              color: "#02061799",
              mb: 0.7,
              fontWeight: "400",
              fontSize: {
                xs: "14px",
                sm: "15px",
                md: "17px",
                lg: "19px",
                xl: "22px",
              },
              fontFamily: "var(--font-outfit)",
              mx: isArabic ? "auto" : { xs: 0, lg: "auto" },
              textAlign: isArabic ? "center" : { xs: "left", lg: "center" },
              maxWidth: { xs: "100%", lg: 900 },
              mt: { xs: 1.5, sm: 2, md: 2.5 },
              lineHeight: 1.5,
            }}
          >
            {t("aiToolkit.subtitle1")}
          </Typography>
        </Box>
      </Container>

      {/* Top Divider */}
      <Box
        sx={{
          width: "100vw",
          height: "1px",
          bgcolor: "#E5E7EB",
          mb: { xs: 2, sm: 2.5, md: 3, lg: 2 },
          position: "relative",
          left: "50%",
          right: "50%",
          marginLeft: "-50vw",
          marginRight: "-50vw",
        }}
      />

      <Container maxWidth="xl">
        {/* Tabs */}
        <Box
          sx={{
            display: "flex",
            justifyContent: { xs: "center", md: "flex-start" },
            gap: { xs: 1.2, sm: 1.5, md: 2, lg: 2.5, xl: 3.5 },
            mb: { xs: 2, sm: 2.5, md: 3, lg: 4 },
            overflowX: "auto",
            flexWrap: "nowrap",
            pb: 2,
            px: { xs: 1.5, sm: 2, md: 0 },
            position: "relative",
            scrollbarWidth: "thin",
            scrollbarColor: "transparent transparent",
            "&::-webkit-scrollbar": { height: "5px" },
            "&::-webkit-scrollbar-track": { backgroundColor: "transparent" },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "transparent",
              borderRadius: "3px",
            },
            "&:hover": {
              scrollbarColor: "#9048FB88 transparent",
            },
            "&:hover::-webkit-scrollbar-thumb": {
              backgroundColor: "#9048FB88",
            },
          }}
        >
          {/* Mobile Arrow - Left (prev) */}
          <Box
            sx={{
              display: { xs: "flex", md: "none" },
              alignItems: "center",
              justifyContent: "center",
              position: "absolute",
              left: 0,
              width: "40px",
              height: "100%",
              zIndex: 10,
              mt: "-2%",
            }}
          >
            <Button
              aria-label="previous tab"
              onClick={() =>
                handleTabChange(
                  activeTab === 0 ? tabs.length - 1 : activeTab - 1,
                )
              }
              sx={{
                minWidth: "40px",
                width: "40px",
                height: "40px",
                borderRadius: "50%",
                bgcolor: "white",
                border: "1px solid rgba(0,0,0,0.1)",
                "&:hover": { bgcolor: "#f5f5f5" },
              }}
            >
              <ChevronRightIcon
                sx={{
                  transform: isArabic ? "none" : "rotate(180deg)",
                  color: "#9048FB",
                  fontSize: 24,
                }}
              />
            </Button>
          </Box>

          {/* Mobile Arrow - Right (next) */}
          <Box
            sx={{
              display: { xs: "flex", md: "none" },
              alignItems: "center",
              justifyContent: "center",
              position: "absolute",
              right: 0,
              width: "40px",
              height: "100%",
              zIndex: 10,
              mt: "-2%",
            }}
          >
            <Button
              aria-label="next tab"
              onClick={() =>
                handleTabChange(
                  activeTab === tabs.length - 1 ? 0 : activeTab + 1,
                )
              }
              sx={{
                minWidth: "40px",
                width: "40px",
                height: "40px",
                borderRadius: "50%",
                bgcolor: "white",
                border: "1px solid rgba(0,0,0,0.1)",
                "&:hover": { bgcolor: "#f5f5f5" },
              }}
            >
              <ChevronRightIcon
                sx={{
                  transform: isArabic ? "rotate(180deg)" : "none",
                  color: "#9048FB",
                  fontSize: 24,
                }}
              />
            </Button>
          </Box>

          {/* Tab Items */}
          {tabs.map((tab) => (
            <Box
              key={tab.id}
              onClick={() => handleTabChange(tab.id)}
              sx={{
                display: {
                  xs: activeTab === tab.id ? "flex" : "none",
                  md: "flex",
                },
                alignItems: "center",
                cursor: "pointer",
                flexShrink: 0,
                minWidth: "fit-content",
                transition: "all 0.3s ease-in-out",
                justifyContent: { xs: "center", md: "flex-start" },
              }}
            >
              <Button
                sx={{
                  bgcolor: "#9048FB",
                  color: "white",
                  px: { xs: 2, sm: 2, md: 2.5, lg: 3.5 },
                  py: { xs: 1.3, sm: 1.4, md: 1.4, lg: 1.4 },
                  borderRadius: "50px",
                  textTransform: "none",
                  fontSize: { xs: "12px", sm: "12px", md: "13px", lg: "14px" },
                  fontWeight: 500,
                  display:
                    activeTab === tab.id ? "flex" : { xs: "none", md: "none" },
                  alignItems: "center",
                  gap: { xs: 0.8, sm: 0.8, md: 1, lg: 1.2 },
                  fontFamily: "var(--font-outfit)",
                  whiteSpace: "nowrap",
                  "&:hover": { bgcolor: "#9048FB" },
                  pointerEvents: { xs: "none", md: "auto" },
                  mt: { xs: "8%", md: "0" },
                }}
              >
                <Box
                  sx={{
                    width: { xs: 20, sm: 20, md: 22, lg: 24 },
                    height: { xs: 20, sm: 20, md: 22, lg: 24 },
                    position: "relative",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Image
                    src={tab.iconLink}
                    alt="Ai ToolKit Icons"
                    width={24}
                    height={24}
                    style={{ objectFit: "contain" }}
                  />
                </Box>
                {t(`aiToolkit.tabs.${tab.tabKey}.label`)}
              </Button>

              <Box
                sx={{
                  display:
                    activeTab === tab.id
                      ? { xs: "none", md: "none" }
                      : { xs: "none", md: "flex" },
                  alignItems: "center",
                  gap: { xs: 0.6, sm: 0.7, md: 0.8, lg: 1 },
                }}
              >
                <Box
                  sx={{
                    bgcolor: "#9048FB",
                    color: "white",
                    borderRadius: "50%",
                    width: { xs: 30, sm: 32, md: 34, lg: 38 },
                    height: { xs: 30, sm: 32, md: 34, lg: 38 },
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: { xs: "6px", sm: "7px", md: "8px", lg: "9px" },
                  }}
                >
                  <Box
                    sx={{ width: "100%", height: "100%", position: "relative" }}
                  >
                    <Image
                      src={tab.iconLink}
                      alt={t(`aiToolkit.tabs.${tab.tabKey}.label`)}
                      fill
                      style={{ objectFit: "contain" }}
                    />
                  </Box>
                </Box>
                <Typography
                  sx={{
                    fontSize: {
                      xs: "11px",
                      sm: "12px",
                      md: "13px",
                      lg: "13px",
                      xl: "14px",
                    },
                    fontWeight: 500,
                    color: "#02061799",
                    fontFamily: "var(--font-outfit)",
                    whiteSpace: "nowrap",
                  }}
                >
                  {t(`aiToolkit.tabs.${tab.tabKey}.label`)}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      </Container>

      {/* Bottom Divider */}
      <Box
        sx={{
          width: "100vw",
          height: "1px",
          bgcolor: "#E5E7EB",
          mb: { xs: 2, sm: 2.5, md: 3, lg: 0 },
          position: "relative",
          left: "50%",
          right: "50%",
          marginLeft: "-50vw",
          marginRight: "-50vw",
          mt: { xs: "-2%", sm: "-2%", md: "-1.5%", lg: "-2%", xl: "-1.5%" },
        }}
      />

      <Container maxWidth="xl">
        {/* Content */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "40% 60%" },
            gap: { xs: 3, sm: 3.5, md: 4, lg: 6 },
            alignItems: "center",
            gridTemplateAreas: {
              xs: "'image' 'text'",
              md: isArabic ? "'image text'" : "'text image'",
            },
          }}
        >
          {/* Left Side - Text */}
          <Box
            sx={{
              gridArea: "text",
              px: { xs: 2, sm: 3, md: 3, lg: 3.5, xl: 2 },
              opacity: isTransitioning ? 0 : 1,
              transform: isTransitioning
                ? `translateX(${isArabic ? "20px" : "-20px"})`
                : "translateX(0)",
              transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
            }}
          >
            <Typography
              variant="h4"
              sx={{
                fontSize: { xs: "15px", sm: "16px", md: "18px", lg: "25px" },
                fontWeight: 400,
                color: "#000000",
                fontFamily: "var(--font-outfit)",
                mb: { xs: 2, sm: 2.5, md: 4, lg: 3 },
                textAlign: isArabic ? "right" : "left",
                mt: '10%'
              }}
            >
              {t(`aiToolkit.tabs.${tabs[activeTab].tabKey}.title`)}
            </Typography>

            <Typography
              sx={{
                fontSize: { xs: "18px", sm: "20px", md: "22px", lg: "26px" },
                color: "#020617",
                lineHeight: 1.5,
                mb: { xs: 2, sm: 2.5, md: 3, lg: 2 },
                fontWeight: 600,
                maxWidth: { xs: "100%", lg: 550 },
                fontFamily: "var(--font-plusjakarta)",
                textAlign: isArabic ? "right" : "left",
              }}
            >
              {t(`aiToolkit.tabs.${tabs[activeTab].tabKey}.heading`)}
            </Typography>

            <Typography
              sx={{
                fontSize: { xs: "13px", sm: "14px", md: "15px", lg: "18px" },
                color: "#6b7280",
                lineHeight: 1.6,
                mb: { xs: 2, sm: 2.5, md: 3, lg: 3 },
                maxWidth: { xs: "100%", lg: 600 },
                fontFamily: "var(--font-outfit)",
                fontWeight: 400,
                textAlign: isArabic ? "right" : "left",
              }}
            >
              {t(`aiToolkit.tabs.${tabs[activeTab].tabKey}.description`)}
            </Typography>

            {/* Feature bullet list - only renders when the active tab has a
                "features" array in the translation file (e.g. tab0) */}
            {featuresList && (
              <Box
                component="ul"
                sx={{
                  listStyle: "none",
                  p: 0,
                  m: 0,
                  mb: { xs: 2, sm: 2.5, md: 3, lg: 1 },
                  display: "grid",
                  gridTemplateColumns: "1fr",
                  rowGap: { xs: 1, sm: 1.2, md: 1.4 },
                }}
              >
                {featuresList.map((feature, i) => (
                  <Box
                    key={i}
                    component="li"
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      fontSize: { xs: "13px", sm: "14px", md: "15px", lg: "17px" },
                      color: "#6b7280",
                      fontFamily: "var(--font-outfit)",
                      fontWeight: 500,
                      textAlign: isArabic ? "right" : "left",
                    }}
                  >
                    <Box
                      sx={{
                        width: 7,
                        height: 7,
                        borderRadius: "50%",
                        bgcolor: "#9048FB",
                        flexShrink: 0,
                      }}
                    />
                    {feature}
                  </Box>
                ))}
              </Box>
            )}

            <NextLink
              href={tabs[activeTab].link}
              passHref
              legacyBehavior
            >
              <Button
                component="a"
                endIcon={
                  <ChevronRightIcon
                    sx={{
                      fontSize: { xs: 18, sm: 20, md: 22 },
                      transform: isArabic ? "rotate(180deg)" : "none",
                      mr: isArabic ? "40%" : 0,
                      mt: isArabic ? "15%" : 0,
                    }}
                  />
                }
                sx={{
                  bgcolor: "#775CE5",
                  color: "white",
                  px: { xs: 2, sm: 2.3, md: 2.5, lg: 3 },
                  py: { xs: 1, sm: 1.1, md: 1.2, lg: 1.4 },
                  borderRadius: "50px",
                  textTransform: "none",
                  fontSize: { xs: "13px", sm: "14px", md: "15px", lg: "16px" },
                  fontWeight: 400,
                  fontFamily: "var(--font-outfit)",
                  mt: { xs: "8%", sm: "10%", md: "12%", lg: "5%" },
                  transition: "all 0.2s ease-in-out",
                  "&:hover": {
                    bgcolor: "#000",
                    transform: "scale(1.05) translateY(-2px)",
                  },
                }}
              >
                {t(`aiToolkit.tabs.${tabs[activeTab].tabKey}.cta`)}
              </Button>
            </NextLink>
          </Box>

          {/* Right Side - Image */}
          <Box
            sx={{
              gridArea: "image",
              position: "relative",
              overflow: "hidden",
              height: { xs: "220px", sm: "280px", md: "380px", lg: "570px" },
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "#f9f9f9",
              transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
            }}
          >
            <Box sx={{ position: "absolute", inset: 0, zIndex: 1 }}>
              <Image
                src={IMAGES.TOOLKIT_BG}
                alt="Background"
                fill
                style={{ objectFit: "cover", objectPosition: "center" }}
                priority
              />
            </Box>
            <Box
              sx={{
                position: "relative",
                zIndex: 2,
                height: "85%",
                width: "85%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Image
                key={tabs[activeTab].id}
                src={tabs[activeTab].image}
                alt={tabs[activeTab].imageAlt}
                fill
                priority
                style={{
                  objectFit: "contain",
                  objectPosition: "center",
                  transition: "opacity 0.4s ease-in-out",
                  opacity: isTransitioning ? 0 : 1,
                }}
              />
            </Box>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default AIToolkit;